import React, { useState, useEffect, useCallback, useRef } from 'react';
import useToast from '../hooks/useToast';
import ToastContainer from './../components/Notification_Toast/ToastContainer.js';
import '../pages-css/AddNewDropdownItems.css';
import { useAuth } from '../hooks/useAuth.js';
import {
  FiEdit2, FiTrash2, FiLayers, FiGitBranch,
  FiBriefcase, FiCheckCircle, FiXCircle, FiRefreshCw,
  FiSearch, FiSave, FiX, FiChevronLeft, FiChevronRight,
  FiChevronsLeft, FiChevronsRight, FiHome,
} from 'react-icons/fi';

/* ── Inline-style theme mappers (added for dark mode) ── */
const __isDarkTheme = () => typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
const __SM = {
  '#fff':'#1b2130','#ffffff':'#1b2130','white':'#1b2130','transparent':'transparent',
  '#f9fafb':'#0f1420','#f8fafc':'#0f1420','#f8f9fa':'#0f1420','#fafafa':'#0f1420','#f8fafb':'#0f1420','#fcfcfd':'#0f1420',
  '#f3f4f6':'#232b3b','#f1f5f9':'#232b3b','#f1f1f1':'#232b3b','#f0f0f0':'#232b3b','#e9eef5':'#2b3445','#eef2f7':'#18202e',
  '#eff6ff':'#15243d','#f0f7ff':'#15243d','#f0f9ff':'#15243d','#f0f4ff':'#1a2440','#eef2ff':'#1e1f45','#dbeafe':'#1d3a5f','#bfdbfe':'#244b7a','#bae6fd':'#16344d','#e0f2fe':'#16344d','#e0e7ff':'#1e2547','#93c5fd':'#2f5d92',
  '#ecfdf5':'#102a22','#f0fdf4':'#14301f','#dcfce7':'#14302a','#d1fae5':'#14302a','#a7f3d0':'#2a5a40','#6ee7b7':'#2a5a40','#bbf7d0':'#2a5a40','#86efac':'#2a5a40',
  '#fef2f2':'#2a1719','#fee2e2':'#3a1f22','#fecaca':'#3a1f22','#fecdd3':'#3a1f26','#fff5f5':'#2b1d20','#fff1f2':'#2b1d20','#fff7ed':'#2c2113','#fffbeb':'#2a2710','#fffdf0':'#2a2710','#fef9c3':'#3a3016','#fef3c7':'#3a3016','#fde68a':'#5a4714','#fef08a':'#5a4714',
  '#f5f3ff':'#241b3d','#faf5ff':'#241b3d','#ede9fe':'#2a2147','#ddd6fe':'#2e2147','#e9d5ff':'#2e2147','#ecfeff':'#103038','#fce7f3':'#3a1f30',
  '#e5e7eb':'#2b3445','#e2e8f0':'#2b3445','#d1d5db':'#3a4456','#cbd5e1':'#3a4456','#a5b4fc':'#3a3d6a','#c4b5fd':'#3a3d6a', '#fcd34d':'#5a4714',};
const __TM = {
  '#0f172a':'#e7ecf3','#111827':'#e7ecf3','#1e293b':'#d4dbe6','#1f2937':'#d4dbe6','#0b1220':'#e7ecf3',
  '#374151':'#c2cbd8','#475569':'#aab4c2','#4b5563':'#aab4c2','#334155':'#aab4c2',
  '#64748b':'#94a1b3','#6b7280':'#94a1b3','#9ca3af':'#9aa7b8','#94a3b8':'#9aa7b8','#718096':'#9aa7b8',
  '#15803d':'#46c46f','#166534':'#6ee7b7','#065f46':'#6ee7b7','#1c4532':'#6ee7b7','#059669':'#18c08a','#16a34a':'#2bc55e','#10b981':'#34d39e',
  '#b45309':'#f0c07a','#c2410c':'#fb923c','#92400e':'#f0c07a','#78350f':'#f0b080','#d97706':'#f0b454','#ca8a04':'#e3c258','#f59e0b':'#f5b945',
  '#b91c1c':'#f08a8a','#991b1b':'#f08a8a','#dc2626':'#f05252','#ef4444':'#f06a6a',
  '#1d4ed8':'#5b9bf0','#2563eb':'#5b9bf0','#1e40af':'#5b9bf0','#3b82f6':'#5b9bf0','#0284c7':'#38bdf8','#0891b2':'#22d3ee','#1e3a8a':'#7fb0f0',
  '#7c3aed':'#a78bfa','#8b5cf6':'#b39bf7','#6d28d9':'#c4b5fd','#5b21b6':'#c4b5fd','#3730a3':'#a5b4fc','#4338ca':'#a5b4fc','#4f46e5':'#8589f3','#6366f1':'#8589f3',
};
const __sbg = (v) => { const k = String(v).toLowerCase(); return (__isDarkTheme() && __SM[k]) ? __SM[k] : v; };
const __stc = (v) => { const k = String(v).toLowerCase(); return (__isDarkTheme() && __TM[k]) ? __TM[k] : v; };
const useThemeVersion = () => {
  const [v, setV] = React.useState(0);
  React.useEffect(() => {
    const obs = new MutationObserver(() => setV(x => x + 1));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return v;
};


const API = process.env.REACT_APP_API_URL;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

/* ── shared headers ──────────────────────────────────────────────── */
const hdrs = () => ({ 'Content-Type': 'application/json' });

const DropdownAdminPage = () => {
  useThemeVersion();
  const [activeTab, setActiveTab] = useState('groups');

  /* ── Refs for height-matching (table card = form card height) ─────── */
  const formCardRef  = useRef(null);
  const tableCardRef = useRef(null);

  // After each tab change (and after initial render), read the form card's
  // rendered height and apply it to the table card so they always match.
  useEffect(() => {
    const sync = () => {
      if (formCardRef.current && tableCardRef.current) {
        const h = formCardRef.current.offsetHeight;
        if (h > 0) tableCardRef.current.style.height = h + 'px';
      }
    };
    // Two passes: immediate (layout may not be final) + after a frame
    sync();
    const id = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(id);
  }, [activeTab]);
  const { user } = useAuth();
  const { toasts, removeToast, showSuccess, showError } = useToast();

  /* ── Pagination state ──────────────────────────────────────────── */
  const [currentPage,   setCurrentPage]   = useState(0);   // 0-indexed (Spring)
  const [pageSize,      setPageSize]      = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages,    setTotalPages]    = useState(0);
  const [search,        setSearch]        = useState('');
  const [searchInput,   setSearchInput]   = useState('');  // debounced

  /* ── Filter states for Sub-Groups and Projects tabs ────────────────── */
  const [filterGroupId,    setFilterGroupId]    = useState(''); // Sub-Groups tab: filter by parent group
  const [filterGroupIdProj, setFilterGroupIdProj] = useState(''); // Projects tab: filter by group
  const [filterSubGroupId, setFilterSubGroupId] = useState(''); // Projects tab: filter by sub-group

  /* ── Table rows ────────────────────────────────────────────────── */
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  /* ── Form dropdown data (always full list) ─────────────────────── */
  const [availableGroups,    setAvailableGroups]    = useState([]);
  const [availableSubGroups, setAvailableSubGroups] = useState([]);
  const [availableProjects,  setAvailableProjects]  = useState([]);
  const [filteredSubs,       setFilteredSubs]       = useState([]);
  // Sub-groups derived from the group picked in the warehouse form
  const [filteredSubsWh,     setFilteredSubsWh]     = useState([]);

  /* Derived: sub-groups of the group selected in the Projects tab filter.
     Must be declared AFTER availableSubGroups to avoid TDZ error. */
  const filteredSubGroupsForProj = filterGroupIdProj
    ? availableSubGroups.filter(sg => sg.group?.id === Number(filterGroupIdProj))
    : availableSubGroups;

  /* ── Forms ─────────────────────────────────────────────────────── */
  const [groupForm, setGroupForm] = useState({
    id: null, groupName: '', groupLabel: '', description: '', isActive: true,
  });
  const [subGroupForm, setSubGroupForm] = useState({
    id: null, subGroupName: '', subGroupLabel: '', description: '', isActive: true, groupId: '',
  });
  const [projectForm, setProjectForm] = useState({
    projectUniqueId: '', projectName: '', description: '', location: '',
    startDate: '', endDate: '', status: 'PLANNING', budget: '', isActive: true,
    selectedGroupId: '', subGroupId: '',
  });
  const [warehouseForm, setWarehouseForm] = useState({
    id: null, code: '', name: '', city: '', address: '', inCharge: '', phone: '',
    groupName: '', subGroupName: '',
    notes: '', isActive: true,
    // local-only — used to drive the cascading selects in the form
    _selectedGroupId: '', _selectedSubGroupId: '',
  });

  const [editModal,     setEditModal]     = useState(null); // { type, data }
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  /* ── Debounce search 400 ms ────────────────────────────────────── */
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setCurrentPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  /* ── Reset on tab switch ───────────────────────────────────────── */
  useEffect(() => {
    setCurrentPage(0); setSearch(''); setSearchInput('');
    setFilterGroupId(''); setFilterGroupIdProj(''); setFilterSubGroupId('');
    setRows([]); setTotalElements(0); setTotalPages(0);
    loadDropdownData();
  }, [activeTab]);

  /* ── Load paginated table data ─────────────────────────────────── */
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage, size: pageSize, search });
      let url = '';
      if (activeTab === 'groups')    url = `${API}/admin/dropdowns/groups?${params}`;
      if (activeTab === 'subgroups') {
        if (filterGroupId)    params.append('groupId', filterGroupId);
        url = `${API}/admin/dropdowns/subgroups?${params}`;
      }
      if (activeTab === 'projects') {
        if (filterGroupIdProj) params.append('groupId',    filterGroupIdProj);
        if (filterSubGroupId)  params.append('subGroupId', filterSubGroupId);
        url = `${API}/admin/dropdowns/projects?${params}`;
      }
      if (activeTab === 'warehouses') {
        url = `${API}/admin/dropdowns/warehouses?${params}`;
      }

      const res  = await fetch(url, { credentials: 'include', headers: hdrs() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();

      setRows(data.content       ?? []);
      setTotalElements(data.totalElements ?? 0);
      setTotalPages(data.totalPages    ?? 0);
    } catch { showError('Failed to load data'); }
    finally  { setLoading(false); }
  }, [activeTab, currentPage, pageSize, search, filterGroupId, filterGroupIdProj, filterSubGroupId]);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Load full lists for form selects ──────────────────────────── */
  const loadDropdownData = async () => {
    try {
      const [gRes, sgRes, pRes] = await Promise.all([
        fetch(`${API}/admin/dropdowns/groups`,    { credentials: 'include', headers: hdrs() }),
        fetch(`${API}/admin/dropdowns/subgroups`, { credentials: 'include', headers: hdrs() }),
        fetch(`${API}/admin/dropdowns/projects`,  { credentials: 'include', headers: hdrs() }),
      ]);
      setAvailableGroups(await gRes.json());
      setAvailableSubGroups(await sgRes.json());
      setAvailableProjects(pRes.ok ? (await pRes.json()) : []);
    } catch {}
  };

  useEffect(() => {
    if (projectForm.selectedGroupId)
      setFilteredSubs(availableSubGroups.filter(sg => sg.group?.id === Number(projectForm.selectedGroupId)));
    else setFilteredSubs([]);
  }, [projectForm.selectedGroupId, availableSubGroups]);

  /* Warehouse form cascading dropdowns:
       group → sub-groups under that group
     Group & sub-group on a warehouse are OPTIONAL (null = global).
     Project is NOT a warehouse field — warehouses are not created under projects. */
  useEffect(() => {
    if (warehouseForm._selectedGroupId)
      setFilteredSubsWh(availableSubGroups.filter(sg => sg.group?.id === Number(warehouseForm._selectedGroupId)));
    else
      setFilteredSubsWh([]);
  }, [warehouseForm._selectedGroupId, availableSubGroups]);

  /* ── Reset forms ───────────────────────────────────────────────── */
  const resetForms = () => {
    setGroupForm({ id: null, groupName: '', groupLabel: '', description: '', isActive: true });
    setSubGroupForm({ id: null, subGroupName: '', subGroupLabel: '', description: '', isActive: true, groupId: '' });
    setProjectForm({ projectUniqueId: '', projectName: '', description: '', location: '', startDate: '', endDate: '', status: 'PLANNING', budget: '', isActive: true, selectedGroupId: '', subGroupId: '' });
    setWarehouseForm({ id: null, code: '', name: '', city: '', address: '', inCharge: '', phone: '', groupName: '', subGroupName: '', notes: '', isActive: true, _selectedGroupId: '', _selectedSubGroupId: '' });
    setFilteredSubs([]); setFilteredSubsWh([]);
  };

  /* ── GROUP handlers ────────────────────────────────────────────── */
  const handleGroupSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await fetch(`${API}/admin/dropdowns/groups`, { credentials: 'include', method: 'POST', headers: hdrs(), body: JSON.stringify(groupForm) });
      if (!res.ok) throw new Error();
      showSuccess('Group created'); resetForms(); loadData(); loadDropdownData();
    } catch { showError('Failed to create group'); } finally { setLoading(false); }
  };
  const handleEditGroupSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    const data = editModal.data;
    try {
      const res = await fetch(`${API}/admin/dropdowns/groups/${data.id}`, { credentials: 'include', method: 'PUT', headers: hdrs(), body: JSON.stringify(data) });
      if (!res.ok) throw new Error();
      showSuccess('Group updated'); setEditModal(null); loadData(); loadDropdownData();
    } catch { showError('Failed to update group'); } finally { setLoading(false); }
  };
  const handleEditGroup   = (g)  => setEditModal({ type: 'group', data: { ...g } });
  const handleDeleteGroup = (id, isActive) => setDeleteConfirm({ type: 'group', url: `${API}/admin/dropdowns/groups/${id}`, label: 'group', isActive });

  /* ── SUBGROUP handlers ─────────────────────────────────────────── */
  const handleSubGroupSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await fetch(`${API}/admin/dropdowns/subgroups?groupId=${subGroupForm.groupId}`, { credentials: 'include', method: 'POST', headers: hdrs(), body: JSON.stringify(subGroupForm) });
      if (!res.ok) throw new Error();
      showSuccess('Sub-Group created'); resetForms(); loadData(); loadDropdownData();
    } catch { showError('Failed to create sub-group'); } finally { setLoading(false); }
  };
  const handleEditSubGroupSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    const data = editModal.data;
    try {
      const res = await fetch(`${API}/admin/dropdowns/subgroups/${data.id}`, { credentials: 'include', method: 'PUT', headers: hdrs(), body: JSON.stringify(data) });
      if (!res.ok) throw new Error();
      showSuccess('Sub-Group updated'); setEditModal(null); loadData(); loadDropdownData();
    } catch { showError('Failed to update sub-group'); } finally { setLoading(false); }
  };
  const handleEditSubGroup   = (sg) => setEditModal({ type: 'subgroup', data: { ...sg, groupId: sg.group?.id || '' } });
  const handleDeleteSubGroup = (id, isActive) => setDeleteConfirm({ type: 'subgroup', url: `${API}/admin/dropdowns/subgroups/${id}`, label: 'sub-group', isActive });

  /* ── PROJECT handlers ──────────────────────────────────────────── */
  const handleProjectSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await fetch(`${API}/admin/dropdowns/projects?subGroupId=${projectForm.subGroupId}&userId=${user.id}`, { credentials: 'include', method: 'POST', headers: hdrs(), body: JSON.stringify(projectForm) });
      if (!res.ok) throw new Error();
      showSuccess('Project created'); resetForms(); loadData();
    } catch { showError('Failed to create project'); } finally { setLoading(false); }
  };
  const handleEditProjectSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    const data = editModal.data;
    try {
      const res = await fetch(`${API}/admin/dropdowns/projects/${data.projectUniqueId}`, { credentials: 'include', method: 'PUT', headers: hdrs(), body: JSON.stringify(data) });
      if (!res.ok) throw new Error();
      showSuccess('Project updated'); setEditModal(null); loadData();
    } catch { showError('Failed to update project'); } finally { setLoading(false); }
  };
  const handleEditProject = (p) => setEditModal({
    type: 'project',
    data: { ...p, selectedGroupId: p.subGroup?.group?.id || '', subGroupId: p.subGroup?.id || '', startDate: p.startDate || '', endDate: p.endDate || '', budget: p.budget || '' }
  });
  const handleDeleteProject = (id, isActive) => setDeleteConfirm({ type: 'project', url: `${API}/admin/dropdowns/projects/${id}`, label: 'project', isActive });

  /* ── WAREHOUSE handlers ────────────────────────────────────────── */
  // Resolves the local _selectedGroupId / _selectedSubGroupId picks into the
  // string fields the backend expects (groupName / subGroupName).
  // Note: projects are NOT a warehouse field — warehouses aren't scoped by project.
  const resolveWarehouseScope = (form) => {
    const group    = availableGroups.find(g => g.id === Number(form._selectedGroupId));
    const subGroup = availableSubGroups.find(sg => sg.id === Number(form._selectedSubGroupId));
    return {
      ...form,
      groupName:    group?.groupName    || '',
      subGroupName: subGroup?.subGroupName || '',
    };
  };

  const handleWarehouseSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const payload = resolveWarehouseScope(warehouseForm);
      // Strip local-only helper fields before sending
      const { _selectedGroupId, _selectedSubGroupId, ...body } = payload;
      const res = await fetch(`${API}/admin/dropdowns/warehouses`, {
        credentials: 'include', method: 'POST', headers: hdrs(), body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || 'Failed');
      }
      showSuccess('Warehouse created'); resetForms(); loadData();
    } catch (err) {
      showError(err.message || 'Failed to create warehouse');
    } finally { setLoading(false); }
  };

  const handleEditWarehouseSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    const data = editModal.data;
    try {
      const payload = resolveWarehouseScope(data);
      const { _selectedGroupId, _selectedSubGroupId, ...body } = payload;
      const res = await fetch(`${API}/admin/dropdowns/warehouses/${data.id}`, {
        credentials: 'include', method: 'PUT', headers: hdrs(), body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message || 'Failed');
      }
      showSuccess('Warehouse updated'); setEditModal(null); loadData();
    } catch (err) {
      showError(err.message || 'Failed to update warehouse');
    } finally { setLoading(false); }
  };

  const handleEditWarehouse = (w) => {
    // Reverse-resolve scope back into local _selectedX ids for the cascading selects
    const group    = availableGroups.find(g => g.groupName === w.groupName);
    const subGroup = availableSubGroups.find(sg =>
      sg.subGroupName === w.subGroupName && (!group || sg.group?.id === group.id));
    setEditModal({
      type: 'warehouse',
      data: {
        ...w,
        _selectedGroupId:    group?.id    || '',
        _selectedSubGroupId: subGroup?.id || '',
        // Coerce nullable strings to '' so React inputs stay controlled
        code: w.code || '', name: w.name || '', city: w.city || '',
        address: w.address || '', inCharge: w.inCharge || '', phone: w.phone || '',
        groupName: w.groupName || '', subGroupName: w.subGroupName || '',
        notes: w.notes || '',
        isActive: w.isActive !== false,
      }
    });
  };
  const handleDeleteWarehouse = (id, isActive) => setDeleteConfirm({
    type: 'warehouse',
    url: `${API}/admin/dropdowns/warehouses/${id}`,
    label: 'warehouse',
    isActive,
  });

  /* ── Confirm delete ────────────────────────────────────────────── */
  const confirmDelete = async () => {
    const { url, type, label } = deleteConfirm; setDeleteConfirm(null); setLoading(true);
    try {
      const res = await fetch(url, { credentials: 'include', method: 'DELETE', headers: hdrs() });
      if (!res.ok) throw new Error();
      showSuccess(`${label.charAt(0).toUpperCase() + label.slice(1)} deleted`);
      loadData();
      // Only refresh the form-driver dropdowns when groups / sub-groups change
      if (type !== 'project' && type !== 'warehouse') loadDropdownData();
    } catch { showError(`Failed to delete ${label}`); } finally { setLoading(false); }
  };

  /* ── Pagination helpers ────────────────────────────────────────── */
  const firstEntry = totalElements === 0 ? 0 : currentPage * pageSize + 1;
  const lastEntry  = Math.min((currentPage + 1) * pageSize, totalElements);

  const visiblePages = () => {
    const pages = [];
    for (let i = 0; i < Math.min(5, totalPages); i++) {
      const p = currentPage < 3 ? i : currentPage + i - 2;
      if (p < 0 || p >= totalPages) continue;
      pages.push(p);
    }
    return pages;
  };

  const STATUS_MAP = { PLANNING:'Planning', IN_PROGRESS:'In Progress', COMPLETED:'Completed', ON_HOLD:'On Hold', CANCELLED:'Cancelled' };

  const tabs = [
    { key:'groups',     label:'Groups',     icon:<FiLayers size={14}/> },
    { key:'subgroups',  label:'Sub-Groups', icon:<FiGitBranch size={14}/> },
    { key:'projects',   label:'Projects',   icon:<FiBriefcase size={14}/> },
    { key:'warehouses', label:'Warehouses', icon:<FiHome size={14}/> },
  ];

  return (
    <div className="da-root">
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* ── Header ── */}
      <div className="da-header">
        <div>
          <p className="da-breadcrumb">Office Use &rsaquo; Dropdown Management</p>
          <h1 className="da-title">Dropdown Management</h1>
          <p className="da-subtitle">Manage groups, sub-groups, projects and warehouses used throughout the CRM</p>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="da-tabs-bar">
        {tabs.map(t => (
          <button key={t.key} className={`da-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => { setActiveTab(t.key); resetForms(); }}>
            {t.icon}{t.label}
            {activeTab === t.key && totalElements > 0 && (
              <span className="da-tab-count">{totalElements}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Two-column body ── */}
      <div className="da-body">

        {/* LEFT – Form card */}
        <div className="da-form-panel">
          <div className="da-form-card" ref={formCardRef}>
            <div className="da-form-card-hd">
              <span className="da-form-card-title">
                Add New {activeTab === 'groups' ? 'Group'
                       : activeTab === 'subgroups' ? 'Sub-Group'
                       : activeTab === 'projects' ? 'Project'
                       : 'Warehouse'}
              </span>
            </div>

            {/* GROUP form */}
            {activeTab === 'groups' && (
              <form onSubmit={handleGroupSubmit} className="da-form">
                <div className="da-row2">
                  <div className="da-field">
                    <label>Group Name <span className="da-req">*</span></label>
                    <input value={groupForm.groupName} onChange={e => setGroupForm({...groupForm, groupName:e.target.value})} required placeholder="e.g. Solar"/>
                  </div>
                  <div className="da-field">
                    <label>Group Label <span className="da-req">*</span></label>
                    <input value={groupForm.groupLabel} onChange={e => setGroupForm({...groupForm, groupLabel:e.target.value})} required placeholder="e.g. Solar Division"/>
                  </div>
                </div>
                <div className="da-field">
                  <label>Description</label>
                  <textarea value={groupForm.description} onChange={e => setGroupForm({...groupForm, description:e.target.value})} rows={3} placeholder="Optional description"/>
                </div>
                <label className="da-check">
                  <input type="checkbox" checked={groupForm.isActive} onChange={e => setGroupForm({...groupForm, isActive:e.target.checked})}/> Active
                </label>
                <div className="da-form-actions">
                  <button type="submit" className="da-btn da-btn-primary" disabled={loading}>
                    <FiSave size={13}/>{loading ? 'Saving…' : 'Create Group'}
                  </button>
                </div>
              </form>
            )}

            {/* SUBGROUP form */}
            {activeTab === 'subgroups' && (
              <form onSubmit={handleSubGroupSubmit} className="da-form">
                <div className="da-field">
                  <label>Parent Group <span className="da-req">*</span></label>
                  <select value={subGroupForm.groupId} onChange={e => setSubGroupForm({...subGroupForm, groupId:e.target.value})} required>
                    <option value="">Select Group</option>
                    {availableGroups.map(g => <option key={g.id} value={g.id}>{g.groupLabel}</option>)}
                  </select>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Sub-Group Name <span className="da-req">*</span></label>
                    <input value={subGroupForm.subGroupName} onChange={e => setSubGroupForm({...subGroupForm, subGroupName:e.target.value})} required placeholder="e.g. Rooftop Solar"/>
                  </div>
                  <div className="da-field">
                    <label>Sub-Group Label <span className="da-req">*</span></label>
                    <input value={subGroupForm.subGroupLabel} onChange={e => setSubGroupForm({...subGroupForm, subGroupLabel:e.target.value})} required placeholder="e.g. Rooftop Solar"/>
                  </div>
                </div>
                <div className="da-field">
                  <label>Description</label>
                  <textarea value={subGroupForm.description} onChange={e => setSubGroupForm({...subGroupForm, description:e.target.value})} rows={3} placeholder="Optional description"/>
                </div>
                <label className="da-check">
                  <input type="checkbox" checked={subGroupForm.isActive} onChange={e => setSubGroupForm({...subGroupForm, isActive:e.target.checked})}/> Active
                </label>
                <div className="da-form-actions">
                  <button type="submit" className="da-btn da-btn-primary" disabled={loading}>
                    <FiSave size={13}/>{loading ? 'Saving…' : 'Create Sub-Group'}
                  </button>
                </div>
              </form>
            )}

            {/* PROJECT form */}
            {activeTab === 'projects' && (
              <form onSubmit={handleProjectSubmit} className="da-form">
                <div className="da-row2">
                  <div className="da-field">
                    <label>Group <span className="da-req">*</span></label>
                    <select value={projectForm.selectedGroupId} onChange={e => setProjectForm({...projectForm, selectedGroupId:e.target.value, subGroupId:''})} required>
                      <option value="">Select Group</option>
                      {availableGroups.map(g => <option key={g.id} value={g.id}>{g.groupLabel}</option>)}
                    </select>
                  </div>
                  <div className="da-field">
                    <label>Sub-Group <span className="da-req">*</span></label>
                    <select value={projectForm.subGroupId} onChange={e => setProjectForm({...projectForm, subGroupId:e.target.value})} required disabled={!projectForm.selectedGroupId}>
                      <option value="">{!projectForm.selectedGroupId ? 'Select Group First' : 'Select Sub-Group'}</option>
                      {filteredSubs.map(sg => <option key={sg.id} value={sg.id}>{sg.subGroupLabel}</option>)}
                    </select>
                  </div>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Project Name <span className="da-req">*</span></label>
                    <input value={projectForm.projectName} onChange={e => setProjectForm({...projectForm, projectName:e.target.value})} required placeholder="e.g. CCMS Nandyal"/>
                  </div>
                  <div className="da-field">
                    <label>Location</label>
                    <input value={projectForm.location} onChange={e => setProjectForm({...projectForm, location:e.target.value})} placeholder="e.g. Nandyal, AP"/>
                  </div>
                </div>
                <div className="da-field">
                  <label>Description</label>
                  <textarea value={projectForm.description} onChange={e => setProjectForm({...projectForm, description:e.target.value})} rows={2} placeholder="Optional description"/>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Start Date</label>
                    <input type="date" value={projectForm.startDate} onChange={e => setProjectForm({...projectForm, startDate:e.target.value})}/>
                  </div>
                  <div className="da-field">
                    <label>End Date</label>
                    <input type="date" value={projectForm.endDate} onChange={e => setProjectForm({...projectForm, endDate:e.target.value})}/>
                  </div>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Status</label>
                    <select value={projectForm.status} onChange={e => setProjectForm({...projectForm, status:e.target.value})}>
                      {Object.entries(STATUS_MAP).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="da-field">
                    <label>Budget (₹)</label>
                    <input type="number" value={projectForm.budget} onChange={e => setProjectForm({...projectForm, budget:e.target.value})} placeholder="e.g. 5000000" step="0.01"/>
                  </div>
                </div>
                <label className="da-check">
                  <input type="checkbox" checked={projectForm.isActive} onChange={e => setProjectForm({...projectForm, isActive:e.target.checked})}/> Active
                </label>
                <div className="da-form-actions">
                  <button type="submit" className="da-btn da-btn-primary" disabled={loading}>
                    <FiSave size={13}/>{loading ? 'Saving…' : 'Create Project'}
                  </button>
                </div>
              </form>
            )}

            {/* WAREHOUSE form */}
            {activeTab === 'warehouses' && (
              <form onSubmit={handleWarehouseSubmit} className="da-form">
                <div className="da-row2">
                  <div className="da-field">
                    <label>Warehouse Code <span className="da-req">*</span></label>
                    <input value={warehouseForm.code} onChange={e => setWarehouseForm({...warehouseForm, code:e.target.value})} required placeholder="e.g. WH-A"/>
                  </div>
                  <div className="da-field">
                    <label>Warehouse Name <span className="da-req">*</span></label>
                    <input value={warehouseForm.name} onChange={e => setWarehouseForm({...warehouseForm, name:e.target.value})} required placeholder="e.g. Warehouse A"/>
                  </div>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>City</label>
                    <input value={warehouseForm.city} onChange={e => setWarehouseForm({...warehouseForm, city:e.target.value})} placeholder="e.g. Hyderabad HQ"/>
                  </div>
                  <div className="da-field">
                    <label>In-Charge</label>
                    <input value={warehouseForm.inCharge} onChange={e => setWarehouseForm({...warehouseForm, inCharge:e.target.value})} placeholder="e.g. Ravi Kumar"/>
                  </div>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Phone</label>
                    <input value={warehouseForm.phone} onChange={e => setWarehouseForm({...warehouseForm, phone:e.target.value})} placeholder="e.g. +91 98xxxxxx"/>
                  </div>
                  <div className="da-field">
                    <label>Address</label>
                    <input value={warehouseForm.address} onChange={e => setWarehouseForm({...warehouseForm, address:e.target.value})} placeholder="Optional"/>
                  </div>
                </div>

                {/* Optional scoping — leave blank for a global warehouse */}
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 4 }}>
                  Scope (optional — leave blank for global)
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Group</label>
                    <select value={warehouseForm._selectedGroupId}
                      onChange={e => setWarehouseForm({...warehouseForm, _selectedGroupId:e.target.value, _selectedSubGroupId:''})}>
                      <option value="">— Global —</option>
                      {availableGroups.map(g => <option key={g.id} value={g.id}>{g.groupLabel || g.groupName}</option>)}
                    </select>
                  </div>
                  <div className="da-field">
                    <label>Sub-Group</label>
                    <select value={warehouseForm._selectedSubGroupId}
                      onChange={e => setWarehouseForm({...warehouseForm, _selectedSubGroupId:e.target.value})}
                      disabled={!warehouseForm._selectedGroupId}>
                      <option value="">{warehouseForm._selectedGroupId ? '— Any —' : 'Select Group First'}</option>
                      {filteredSubsWh.map(sg => <option key={sg.id} value={sg.id}>{sg.subGroupLabel || sg.subGroupName}</option>)}
                    </select>
                  </div>
                </div>

                <div className="da-field">
                  <label>Notes</label>
                  <textarea value={warehouseForm.notes} onChange={e => setWarehouseForm({...warehouseForm, notes:e.target.value})} rows={2} placeholder="Optional notes"/>
                </div>
                <label className="da-check">
                  <input type="checkbox" checked={warehouseForm.isActive} onChange={e => setWarehouseForm({...warehouseForm, isActive:e.target.checked})}/> Active
                </label>
                <div className="da-form-actions">
                  <button type="submit" className="da-btn da-btn-primary" disabled={loading}>
                    <FiSave size={13}/>{loading ? 'Saving…' : 'Create Warehouse'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* RIGHT – Table card */}
        <div className="da-table-panel">
          <div className="da-table-card" ref={tableCardRef} style={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

            {/* Toolbar */}
            <div className="da-toolbar">
              <div className="da-search-wrap">
                <FiSearch size={13} className="da-search-icon"/>
                <input className="da-search" value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder={`Search ${activeTab}…`}/>
                {searchInput && (
                  <button className="da-search-clear" onClick={() => { setSearchInput(''); setSearch(''); setCurrentPage(0); }}>
                    <FiX size={12}/>
                  </button>
                )}
              </div>

              {/* Sub-Groups tab — Parent Group filter */}
              {activeTab === 'subgroups' && (
                <select
                  value={filterGroupId}
                  onChange={e => { setFilterGroupId(e.target.value); setCurrentPage(0); }}
                  style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: `1px solid ${__sbg('#e2e8f0')}`, background: __sbg('#fff'), color: __stc('#374151'), cursor: 'pointer', minWidth: 140 }}
                >
                  <option value="">All Groups</option>
                  {availableGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.groupLabel || g.groupName}</option>
                  ))}
                </select>
              )}

              {/* Projects tab — Group + Sub-Group filters */}
              {activeTab === 'projects' && (
                <>
                  <select
                    value={filterGroupIdProj}
                    onChange={e => { setFilterGroupIdProj(e.target.value); setFilterSubGroupId(''); setCurrentPage(0); }}
                    style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: `1px solid ${__sbg('#e2e8f0')}`, background: __sbg('#fff'), color: __stc('#374151'), cursor: 'pointer', minWidth: 130 }}
                  >
                    <option value="">All Groups</option>
                    {availableGroups.map(g => (
                      <option key={g.id} value={g.id}>{g.groupLabel || g.groupName}</option>
                    ))}
                  </select>
                  <select
                    value={filterSubGroupId}
                    onChange={e => { setFilterSubGroupId(e.target.value); setCurrentPage(0); }}
                    disabled={!filterGroupIdProj}
                    style={{ fontSize: 12, padding: '5px 10px', borderRadius: 6, border: `1px solid ${__sbg('#e2e8f0')}`, background: filterGroupIdProj ? __sbg('#fff') : __sbg('#f8fafc'), color: __stc('#374151'), cursor: filterGroupIdProj ? 'pointer' : 'default', minWidth: 140, opacity: filterGroupIdProj ? 1 : 0.6 }}
                  >
                    <option value="">{filterGroupIdProj ? 'All Sub-Groups' : 'Select Group First'}</option>
                    {filteredSubGroupsForProj.map(sg => (
                      <option key={sg.id} value={sg.id}>{sg.subGroupLabel || sg.subGroupName}</option>
                    ))}
                  </select>
                </>
              )}

              {/* Warehouses tab — no project filter (warehouses aren't scoped by project) */}

              <button className="da-btn da-btn-icon" title="Refresh" onClick={loadData}>
                <FiRefreshCw size={13} className={loading ? 'da-spin' : ''}/>
              </button>
            </div>

            {/* Fixed-height scrollable table */}
            <div className="da-tbl-scroll">

              {activeTab === 'groups' && (
                <table className="da-tbl">
                  <thead><tr>
                    <th>ID</th><th>Name</th><th>Label</th><th>Description</th><th>Status</th><th>Actions</th>
                  </tr></thead>
                  <tbody>
                    {loading ? <tr><td colSpan={6} className="da-empty">Loading…</td></tr>
                    : rows.length === 0 ? <tr><td colSpan={6} className="da-empty">No groups found</td></tr>
                    : rows.map(g => (
                      <tr key={g.id}>
                        <td className="da-mono">{g.id}</td>
                        <td className="da-bold">{g.groupName}</td>
                        <td>{g.groupLabel}</td>
                        <td className="da-muted">{g.description || '—'}</td>
                        <td>{g.isActive
                          ? <span className="da-badge da-green"><FiCheckCircle size={10}/>Active</span>
                          : <span className="da-badge da-red"><FiXCircle size={10}/>Inactive</span>}
                        </td>
                        <td><div className="da-acts">
                          <button className="da-act da-act-edit" title="Edit" onClick={() => handleEditGroup(g)}><FiEdit2 size={13}/></button>
                          <button className="da-act da-act-del" title="Delete" onClick={() => handleDeleteGroup(g.id, g.isActive)}><FiTrash2 size={13}/></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {activeTab === 'subgroups' && (
                <table className="da-tbl">
                  <thead><tr>
                    <th>ID</th><th>Group</th><th>Name</th><th>Label</th><th>Description</th><th>Status</th><th>Actions</th>
                  </tr></thead>
                  <tbody>
                    {loading ? <tr><td colSpan={7} className="da-empty">Loading…</td></tr>
                    : rows.length === 0 ? <tr><td colSpan={7} className="da-empty">No sub-groups found</td></tr>
                    : rows.map(sg => (
                      <tr key={sg.id}>
                        <td className="da-mono">{sg.id}</td>
                        <td><span className="da-chip">{sg.group?.groupName || '—'}</span></td>
                        <td className="da-bold">{sg.subGroupName}</td>
                        <td>{sg.subGroupLabel}</td>
                        <td className="da-muted">{sg.description || '—'}</td>
                        <td>{sg.isActive
                          ? <span className="da-badge da-green"><FiCheckCircle size={10}/>Active</span>
                          : <span className="da-badge da-red"><FiXCircle size={10}/>Inactive</span>}
                        </td>
                        <td><div className="da-acts">
                          <button className="da-act da-act-edit" title="Edit" onClick={() => handleEditSubGroup(sg)}><FiEdit2 size={13}/></button>
                          <button className="da-act da-act-del" title="Delete" onClick={() => handleDeleteSubGroup(sg.id, sg.isActive)}><FiTrash2 size={13}/></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {activeTab === 'projects' && (
                <table className="da-tbl">
                  <thead><tr>
                    <th>Project ID</th><th>Name</th><th>Group</th><th>Sub-Group</th>
                    <th>Location</th><th>Status</th><th>Budget</th><th>Active</th><th>Actions</th>
                  </tr></thead>
                  <tbody>
                    {loading ? <tr><td colSpan={9} className="da-empty">Loading…</td></tr>
                    : rows.length === 0 ? <tr><td colSpan={9} className="da-empty">No projects found</td></tr>
                    : rows.map(p => (
                      <tr key={p.id}>
                        <td className="da-mono">{p.projectUniqueId}</td>
                        <td className="da-bold">{p.projectName}</td>
                        <td><span className="da-chip">{p.subGroup?.group?.groupName || '—'}</span></td>
                        <td><span className="da-chip da-chip-blue">{p.subGroup?.subGroupName || '—'}</span></td>
                        <td className="da-muted">{p.location || '—'}</td>
                        <td><span className={`da-badge da-status-${(p.status||'').toLowerCase()}`}>{STATUS_MAP[p.status] || p.status}</span></td>
                        <td className="da-amount">{p.budget ? `₹${Number(p.budget).toLocaleString('en-IN')}` : '—'}</td>
                        <td>{p.isActive
                          ? <span className="da-badge da-green"><FiCheckCircle size={10}/>Yes</span>
                          : <span className="da-badge da-red"><FiXCircle size={10}/>No</span>}
                        </td>
                        <td><div className="da-acts">
                          <button className="da-act da-act-edit" title="Edit" onClick={() => handleEditProject(p)}><FiEdit2 size={13}/></button>
                          <button className="da-act da-act-del" title="Delete" onClick={() => handleDeleteProject(p.projectUniqueId, p.isActive)}><FiTrash2 size={13}/></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {activeTab === 'warehouses' && (
                <table className="da-tbl">
                  <thead><tr>
                    <th>ID</th><th>Code</th><th>Name</th><th>City</th>
                    <th>In-Charge</th><th>Scope</th><th>Status</th><th>Actions</th>
                  </tr></thead>
                  <tbody>
                    {loading ? <tr><td colSpan={8} className="da-empty">Loading…</td></tr>
                    : rows.length === 0 ? <tr><td colSpan={8} className="da-empty">No warehouses found</td></tr>
                    : rows.map(w => (
                      <tr key={w.id}>
                        <td className="da-mono">{w.id}</td>
                        <td className="da-mono da-bold">{w.code}</td>
                        <td className="da-bold">{w.name}</td>
                        <td className="da-muted">{w.city || '—'}</td>
                        <td className="da-muted">{w.inCharge || '—'}</td>
                        <td>
                          {(w.groupName || w.subGroupName)
                            ? <span className="da-chip da-chip-blue">
                                {w.groupName || '—'}{w.subGroupName ? ` › ${w.subGroupName}` : ''}
                              </span>
                            : <span className="da-chip" style={{ background: '#f1f5f9', color: '#64748b' }}>Global</span>}
                        </td>
                        <td>{w.isActive
                          ? <span className="da-badge da-green"><FiCheckCircle size={10}/>Active</span>
                          : <span className="da-badge da-red"><FiXCircle size={10}/>Inactive</span>}
                        </td>
                        <td><div className="da-acts">
                          <button className="da-act da-act-edit" title="Edit" onClick={() => handleEditWarehouse(w)}><FiEdit2 size={13}/></button>
                          <button className="da-act da-act-del" title="Delete" onClick={() => handleDeleteWarehouse(w.id, w.isActive)}><FiTrash2 size={13}/></button>
                        </div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* ── Pagination footer ── */}
            <div className="da-tbl-footer">
              <div className="da-pagination-info">
                Showing <strong>{firstEntry}</strong> to <strong>{lastEntry}</strong> of <strong>{totalElements}</strong> entries
                <select className="da-page-size-select" value={pageSize}
                  onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(0); }}>
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n} Rows</option>)}
                </select>
              </div>

              <div className="da-pagination">
                <button className="da-page-btn" onClick={() => setCurrentPage(0)} disabled={currentPage === 0} title="First">
                  <FiChevronsLeft size={13}/>
                </button>
                <button className="da-page-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 0} title="Previous">
                  <FiChevronLeft size={13}/>
                </button>
                {visiblePages().map(p => (
                  <button key={p} className={`da-page-btn${p === currentPage ? ' active' : ''}`}
                    onClick={() => setCurrentPage(p)}>{p + 1}</button>
                ))}
                <button className="da-page-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage >= totalPages - 1} title="Next">
                  <FiChevronRight size={13}/>
                </button>
                <button className="da-page-btn" onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1} title="Last">
                  <FiChevronsRight size={13}/>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ── Edit Modal ── */}
      {editModal && (
        <div className="da-overlay">
          <div className="da-modal da-modal-edit">

            {/* Modal header */}
            <div className="da-modal-edit-hd">
              <span className="da-modal-edit-title">
                Edit {editModal.type === 'group' ? 'Group'
                    : editModal.type === 'subgroup' ? 'Sub-Group'
                    : editModal.type === 'project'  ? 'Project'
                    : 'Warehouse'}
              </span>
              <button className="da-icon-btn" onClick={() => setEditModal(null)} title="Close"><FiX size={14}/></button>
            </div>

            {/* ── Edit Group form ── */}
            {editModal.type === 'group' && (
              <form onSubmit={handleEditGroupSubmit} className="da-form da-modal-form">
                <div className="da-row2">
                  <div className="da-field">
                    <label>Group Name <span className="da-req">*</span></label>
                    <input value={editModal.data.groupName} onChange={e => setEditModal(m => ({...m, data:{...m.data, groupName:e.target.value}}))} required/>
                  </div>
                  <div className="da-field">
                    <label>Group Label <span className="da-req">*</span></label>
                    <input value={editModal.data.groupLabel} onChange={e => setEditModal(m => ({...m, data:{...m.data, groupLabel:e.target.value}}))} required/>
                  </div>
                </div>
                <div className="da-field">
                  <label>Description</label>
                  <textarea value={editModal.data.description || ''} onChange={e => setEditModal(m => ({...m, data:{...m.data, description:e.target.value}}))} rows={3}/>
                </div>
                <label className="da-check">
                  <input type="checkbox" checked={editModal.data.isActive} onChange={e => setEditModal(m => ({...m, data:{...m.data, isActive:e.target.checked}}))}/>  Active
                </label>
                <div className="da-modal-edit-footer">
                  <button type="button" className="da-btn da-btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
                  <button type="submit" className="da-btn da-btn-primary" disabled={loading}>
                    <FiSave size={13}/>{loading ? 'Saving…' : 'Update Group'}
                  </button>
                </div>
              </form>
            )}

            {/* ── Edit Sub-Group form ── */}
            {editModal.type === 'subgroup' && (
              <form onSubmit={handleEditSubGroupSubmit} className="da-form da-modal-form">
                <div className="da-field">
                  <label>Parent Group <span className="da-req">*</span></label>
                  <select value={editModal.data.groupId} onChange={e => setEditModal(m => ({...m, data:{...m.data, groupId:e.target.value}}))} required>
                    <option value="">Select Group</option>
                    {availableGroups.map(g => <option key={g.id} value={g.id}>{g.groupLabel}</option>)}
                  </select>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Sub-Group Name <span className="da-req">*</span></label>
                    <input value={editModal.data.subGroupName} onChange={e => setEditModal(m => ({...m, data:{...m.data, subGroupName:e.target.value}}))} required/>
                  </div>
                  <div className="da-field">
                    <label>Sub-Group Label <span className="da-req">*</span></label>
                    <input value={editModal.data.subGroupLabel} onChange={e => setEditModal(m => ({...m, data:{...m.data, subGroupLabel:e.target.value}}))} required/>
                  </div>
                </div>
                <div className="da-field">
                  <label>Description</label>
                  <textarea value={editModal.data.description || ''} onChange={e => setEditModal(m => ({...m, data:{...m.data, description:e.target.value}}))} rows={3}/>
                </div>
                <label className="da-check">
                  <input type="checkbox" checked={editModal.data.isActive} onChange={e => setEditModal(m => ({...m, data:{...m.data, isActive:e.target.checked}}))}/>  Active
                </label>
                <div className="da-modal-edit-footer">
                  <button type="button" className="da-btn da-btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
                  <button type="submit" className="da-btn da-btn-primary" disabled={loading}>
                    <FiSave size={13}/>{loading ? 'Saving…' : 'Update Sub-Group'}
                  </button>
                </div>
              </form>
            )}

            {/* ── Edit Project form ── */}
            {editModal.type === 'project' && (
              <form onSubmit={handleEditProjectSubmit} className="da-form da-modal-form">
                <div className="da-row2">
                  <div className="da-field">
                    <label>Project ID</label>
                    <input value={editModal.data.projectUniqueId} disabled className="da-disabled"/>
                  </div>
                  <div className="da-field">
                    <label>Project Name <span className="da-req">*</span></label>
                    <input value={editModal.data.projectName} onChange={e => setEditModal(m => ({...m, data:{...m.data, projectName:e.target.value}}))} required/>
                  </div>
                </div>
                <div className="da-field">
                  <label>Location</label>
                  <input value={editModal.data.location || ''} onChange={e => setEditModal(m => ({...m, data:{...m.data, location:e.target.value}}))}/>
                </div>
                <div className="da-field">
                  <label>Description</label>
                  <textarea value={editModal.data.description || ''} onChange={e => setEditModal(m => ({...m, data:{...m.data, description:e.target.value}}))} rows={2}/>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Start Date</label>
                    <input type="date" value={editModal.data.startDate || ''} onChange={e => setEditModal(m => ({...m, data:{...m.data, startDate:e.target.value}}))}/>
                  </div>
                  <div className="da-field">
                    <label>End Date</label>
                    <input type="date" value={editModal.data.endDate || ''} onChange={e => setEditModal(m => ({...m, data:{...m.data, endDate:e.target.value}}))}/>
                  </div>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Status</label>
                    <select value={editModal.data.status || 'PLANNING'} onChange={e => setEditModal(m => ({...m, data:{...m.data, status:e.target.value}}))}>
                      {Object.entries(STATUS_MAP).map(([v,l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div className="da-field">
                    <label>Budget (₹)</label>
                    <input type="number" value={editModal.data.budget || ''} onChange={e => setEditModal(m => ({...m, data:{...m.data, budget:e.target.value}}))} step="0.01"/>
                  </div>
                </div>
                <label className="da-check">
                  <input type="checkbox" checked={editModal.data.isActive} onChange={e => setEditModal(m => ({...m, data:{...m.data, isActive:e.target.checked}}))}/>  Active
                </label>
                <div className="da-modal-edit-footer">
                  <button type="button" className="da-btn da-btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
                  <button type="submit" className="da-btn da-btn-primary" disabled={loading}>
                    <FiSave size={13}/>{loading ? 'Saving…' : 'Update Project'}
                  </button>
                </div>
              </form>
            )}

            {/* ── Edit Warehouse form ── */}
            {editModal.type === 'warehouse' && (
              <form onSubmit={handleEditWarehouseSubmit} className="da-form da-modal-form">
                <div className="da-row2">
                  <div className="da-field">
                    <label>Warehouse Code <span className="da-req">*</span></label>
                    <input value={editModal.data.code}
                      onChange={e => setEditModal(m => ({...m, data:{...m.data, code:e.target.value}}))} required/>
                  </div>
                  <div className="da-field">
                    <label>Warehouse Name <span className="da-req">*</span></label>
                    <input value={editModal.data.name}
                      onChange={e => setEditModal(m => ({...m, data:{...m.data, name:e.target.value}}))} required/>
                  </div>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>City</label>
                    <input value={editModal.data.city}
                      onChange={e => setEditModal(m => ({...m, data:{...m.data, city:e.target.value}}))}/>
                  </div>
                  <div className="da-field">
                    <label>In-Charge</label>
                    <input value={editModal.data.inCharge}
                      onChange={e => setEditModal(m => ({...m, data:{...m.data, inCharge:e.target.value}}))}/>
                  </div>
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Phone</label>
                    <input value={editModal.data.phone}
                      onChange={e => setEditModal(m => ({...m, data:{...m.data, phone:e.target.value}}))}/>
                  </div>
                  <div className="da-field">
                    <label>Address</label>
                    <input value={editModal.data.address}
                      onChange={e => setEditModal(m => ({...m, data:{...m.data, address:e.target.value}}))}/>
                  </div>
                </div>

                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 4 }}>
                  Scope (optional — leave blank for global)
                </div>
                <div className="da-row2">
                  <div className="da-field">
                    <label>Group</label>
                    <select value={editModal.data._selectedGroupId}
                      onChange={e => setEditModal(m => ({...m, data:{...m.data, _selectedGroupId:e.target.value, _selectedSubGroupId:''}}))}>
                      <option value="">— Global —</option>
                      {availableGroups.map(g => <option key={g.id} value={g.id}>{g.groupLabel || g.groupName}</option>)}
                    </select>
                  </div>
                  <div className="da-field">
                    <label>Sub-Group</label>
                    <select value={editModal.data._selectedSubGroupId}
                      onChange={e => setEditModal(m => ({...m, data:{...m.data, _selectedSubGroupId:e.target.value}}))}
                      disabled={!editModal.data._selectedGroupId}>
                      <option value="">{editModal.data._selectedGroupId ? '— Any —' : 'Select Group First'}</option>
                      {availableSubGroups
                        .filter(sg => sg.group?.id === Number(editModal.data._selectedGroupId))
                        .map(sg => <option key={sg.id} value={sg.id}>{sg.subGroupLabel || sg.subGroupName}</option>)}
                    </select>
                  </div>
                </div>

                <div className="da-field">
                  <label>Notes</label>
                  <textarea value={editModal.data.notes}
                    onChange={e => setEditModal(m => ({...m, data:{...m.data, notes:e.target.value}}))} rows={2}/>
                </div>
                <label className="da-check">
                  <input type="checkbox" checked={editModal.data.isActive}
                    onChange={e => setEditModal(m => ({...m, data:{...m.data, isActive:e.target.checked}}))}/> Active
                </label>
                <div className="da-modal-edit-footer">
                  <button type="button" className="da-btn da-btn-ghost" onClick={() => setEditModal(null)}>Cancel</button>
                  <button type="submit" className="da-btn da-btn-primary" disabled={loading}>
                    <FiSave size={13}/>{loading ? 'Saving…' : 'Update Warehouse'}
                  </button>
                </div>
              </form>
            )}

          </div>
        </div>
      )}

      {/* ── Delete Modal ── */}
      {deleteConfirm && (
        <div className="da-overlay">
          <div className="da-modal">
            <div className="da-modal-icon" style={{ background: deleteConfirm.isActive ? __sbg('#fffbeb') : __sbg('#fef2f2'), borderColor: deleteConfirm.isActive ? __sbg('#fcd34d') : __sbg('#fecaca') }}>
              <FiTrash2 size={26} color={deleteConfirm.isActive ? '#d97706' : '#dc2626'}/>
            </div>
            <h3 className="da-modal-title">
              {deleteConfirm.isActive ? 'Deactivate' : 'Permanently Delete'} {deleteConfirm.label.charAt(0).toUpperCase()+deleteConfirm.label.slice(1)}
            </h3>
            <p className="da-modal-msg">
              {deleteConfirm.isActive ? (
                <>
                  This {deleteConfirm.label} is currently <strong>active</strong>.<br/>
                  It will be marked as <strong style={{color:__stc('#d97706')}}>inactive</strong> and hidden from dropdowns.<br/>
                  <span style={{fontSize:12,color:__stc('#94a3b8')}}>Delete again when inactive to permanently remove it.</span>
                </>
              ) : (
                <>
                  This {deleteConfirm.label} is already <strong style={{color:__stc('#dc2626')}}>inactive</strong>.<br/>
                  It will be <strong style={{color:__stc('#dc2626')}}>permanently deleted</strong> along with all related data.<br/>
                  <strong style={{color:__stc('#dc2626')}}>This action cannot be undone.</strong>
                </>
              )}
            </p>
            <div className="da-modal-actions">
              <button className="da-btn da-btn-ghost" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button
                className="da-btn"
                style={{ background: deleteConfirm.isActive ? __sbg('#d97706') : __sbg('#dc2626'), color: __stc('#fff'), flex: 1, justifyContent: 'center' }}
                onClick={confirmDelete}>
                {deleteConfirm.isActive ? 'Mark Inactive' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownAdminPage;